import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { adminApi } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { errorMessage } from "./first-round";
import { useAdminProduct } from "./hooks";
import { useConfirm } from "./ui/ConfirmDialog";
import { type Column, DataTable } from "./ui/DataTable";
import { PageHeader } from "./ui/PageHeader";

export function FeaturesManagement() {
  const { product } = useAdminProduct();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const { data: features, isLoading, error } = useQuery({
    queryKey: ["features", product],
    queryFn: () => adminApi.listFeatures(product),
  });

  const mutate = useMutation({
    mutationFn: (payload: { id: string; enabled: boolean }) =>
      adminApi.updateFeature(payload.id, { enabled: payload.enabled }),
    onSuccess: (_, { enabled }) => {
      qc.invalidateQueries({ queryKey: ["features", product] });
      toast({ title: enabled ? "Feature enabled" : "Feature disabled", variant: "success" });
    },
    onError: (e) =>
      toast({ title: "Couldn't change the feature", description: errorMessage(e), variant: "destructive" }),
  });

  const toggle = async (f: any) => {
    // Disabling doesn't hide a feature from the cards: theResume refuses it for everyone.
    if (
      f.enabled &&
      product === "THERESUME" &&
      !(await confirm({
        title: `Disable "${f.name}"?`,
        description:
          "This blocks it for every theResume user: their requests fail. To stop selling it, leave it on and change its limits instead.",
        confirmLabel: "Disable anyway",
        destructive: true,
      }))
    ) {
      return;
    }
    mutate.mutate({ id: f.id, enabled: !f.enabled });
  };

  const columns: Column<any>[] = [
    {
      key: "name",
      header: "Name",
      sortValue: (f) => f.name,
      cell: (f) => (
        <Link to={`/admin/features/${f.id}`} className="font-medium text-slate-900 hover:underline">
          {f.name}
        </Link>
      ),
    },
    {
      key: "key",
      header: "Key",
      sortValue: (f) => f.key,
      cell: (f) => <code className="font-mono text-xs text-slate-600">{f.key}</code>,
    },
    {
      key: "enabled",
      header: "Status",
      sortValue: (f) => (f.enabled ? 0 : 1),
      cell: (f) => (
        <Badge variant={f.enabled ? "success" : "secondary"}>{f.enabled ? "Enabled" : "Disabled"}</Badge>
      ),
    },
    {
      key: "actions",
      header: <span className="sr-only">Actions</span>,
      className: "text-right",
      cell: (f) => (
        <div className="flex justify-end gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={`/admin/features/${f.id}`}>Limits & usage</Link>
          </Button>
          <Button
            variant={f.enabled ? "ghost" : "outline"}
            size="sm"
            disabled={mutate.isPending}
            onClick={() => toggle(f)}
          >
            {f.enabled ? "Disable" : "Enable"}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Features & limits"
        description="Billable features and how much of each a plan includes. Release switches for unfinished work are on Feature flags; theResume's sidebar items are on theResume menu."
      />
      <DataTable
        label="Features"
        columns={columns}
        rows={features}
        rowKey={(f) => f.id}
        isLoading={isLoading}
        error={error}
        emptyMessage="No features for this product."
        pageSize={null}
      />
      {confirmDialog}
    </div>
  );
}
